import { useState, useEffect, useRef, useMemo } from 'react';
import { client, urlFor } from '../lib/sanity';
import Modal from 'react-modal';
import { motion, AnimatePresence } from 'framer-motion';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';

Modal.setAppElement('#root');

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.12 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };

function useDebouncedValue(value, delay = 200) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
}

const getProviderInfo = (video) => {
    if (video.provider) return { name: video.provider, color: 'bg-red-500' };
    if (video.videoType === 'embed') {
        if (video.embedUrl?.includes('youtube')) return { name: 'YouTube', color: 'bg-red-500' };
        if (video.embedUrl?.includes('vimeo')) return { name: 'Vimeo', color: 'bg-blue-500' };
    }
    if (video.videoType === 'upload') return { name: 'Upload', color: 'bg-purple-500' };
    return { name: 'Video', color: 'bg-zinc-500' };
};

const Videos = () => {
    const [videos, setVideos] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);
    const closeBtnRef = useRef(null);

    const [selectedTag, setSelectedTag] = useState('');
    const [sort, setSort] = useState('newest'); // newest | oldest | title-asc

    useEffect(() => {
        let active = true;
        setLoading(true);
        client.fetch(`*[_type == "videoGallery"] | order(_createdAt desc){
          _id,
          title,
          description,
          _createdAt,
          videoType,
          embedUrl,
          thumbnail { asset->{ url, metadata{ lqip } } },
          videoFile { asset->{ url } },
          tags,
          provider,
          duration
      }`).then((res) => {
            if (!active) return;
            setVideos(res || []);
            setFetchError(!(res && res.length));
        }).catch(() => {
            if (!active) return;
            setFetchError(true);
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false };
    }, []);

    const handleClick = (video) => {
        setSelectedVideo(video);
        if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.set('v', video._id);
            window.history.replaceState(null, '', url.toString());
        }
    };
    const closeModal = () => {
        setSelectedVideo(null);
        if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.delete('v');
            window.history.replaceState(null, '', url.toString());
        }
    };

    useEffect(() => {
        if (selectedVideo && closeBtnRef.current) {
            // wait one frame so Modal has mounted
            const id = requestAnimationFrame(() => closeBtnRef.current?.focus());
            return () => cancelAnimationFrame(id);
        }
    }, [selectedVideo]);

    // Open from URL (?v=...)
    useEffect(() => {
        const id = new URLSearchParams(window.location.search).get('v');
        if (!id || !videos?.length) return;
        const v = videos.find(x => x._id === id);
        if (v) setSelectedVideo(v);
    }, [videos]);

    const debouncedSearch = useDebouncedValue(searchTerm, 200);
    const allTags = useMemo(() => {
        const set = new Set();
        videos.forEach(v => (v.tags || []).forEach(t => t && set.add(t)));
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [videos]);

    const filteredVideos = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase();
        let list = videos.filter(v => {
            const matchText = !q || [v.title, v.description].filter(Boolean).some(s => s.toLowerCase().includes(q));
            const matchTag = !selectedTag || (v.tags || []).includes(selectedTag);
            return matchText && matchTag;
        });
        if (sort === 'oldest') list.sort((a, b) => new Date(a._createdAt) - new Date(b._createdAt));
        else if (sort === 'title-asc') list.sort((a, b) => a.title.localeCompare(b.title));
        else list.sort((a, b) => new Date(b._createdAt) - new Date(a._createdAt)); // newest
        return list;
    }, [videos, debouncedSearch, selectedTag, sort]);

    // Arrow key next/prev while modal is open
    useEffect(() => {
        if (!selectedVideo) return;
        const onKey = (e) => {
            const idx = filteredVideos.findIndex(v => v._id === selectedVideo._id);
            if (idx === -1 || !filteredVideos.length) return;
            if (e.key === 'ArrowRight') {
                const next = filteredVideos[(idx + 1) % filteredVideos.length];
                handleClick(next);
            }
            if (e.key === 'ArrowLeft') {
                const prev = filteredVideos[(idx - 1 + filteredVideos.length) % filteredVideos.length];
                handleClick(prev);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedVideo, filteredVideos]);

    return (
        <div className="min-h-screen bg-white dark:bg-zinc-950">
            <div className="max-w-7xl mx-auto px-4 py-12">
                <h1 className="text-5xl md:text-6xl font-serif font-bold mb-10 max-w-2xl text-black dark:text-white">Video Gallery</h1>
                {(selectedTag || searchTerm) && (
                    <p className="text-zinc-700 dark:text-zinc-300 mb-2">
                        Showing <strong>{filteredVideos.length}</strong> result{filteredVideos.length === 1 ? '' : 's'}{selectedTag ? ` for “${selectedTag}”` : ''}
                    </p>
                )}
                <motion.div variants={container} initial="hidden" animate="show" className="mb-8">
                    {/* Search and Sort Row */}
                    <motion.div variants={item} className="flex flex-wrap gap-3 items-center mb-6">
                        <Input
                            type="text"
                            placeholder="Search by title or description"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full max-w-xl"
                        />
                        <Select value={sort} onChange={(e) => setSort(e.target.value)} wrapperClassName="w-auto">
                            <option value="newest">Newest</option>
                            <option value="oldest">Oldest</option>
                            <option value="title-asc">Title A–Z</option>
                        </Select>
                    </motion.div>

                    {/* Active Filters Indicator */}
                    {(selectedTag || searchTerm) && (
                        <motion.div variants={item} className="flex flex-wrap gap-2 items-center mb-6 px-4 py-3 bg-white dark:bg-zinc-950 border-2 border-black dark:border-white">
                            <span className="text-xs font-mono font-semibold text-black dark:text-white">Active Filters:</span>
                            {searchTerm && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 border-2 border-accent text-accent text-xs font-mono font-medium">
                                    "{searchTerm}"
                                </span>
                            )}
                            {selectedTag && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 border-2 border-accent text-accent text-xs font-mono font-medium">
                                    {selectedTag}
                                </span>
                            )}
                            <Button
                                variant="ghost"
                                className="ml-auto text-xs"
                                onClick={() => { setSelectedTag(''); setSearchTerm(''); setSort('newest'); }}
                            >
                                Clear All ✕
                            </Button>
                        </motion.div>
                    )}

                    {/* Tag Cloud */}
                    {allTags.length > 0 && (
                        <motion.div variants={item} className="flex flex-wrap gap-2 px-4 py-4 bg-white dark:bg-zinc-950 border-2 border-black dark:border-white">
                            <span className="text-xs font-mono font-semibold text-zinc-600 dark:text-zinc-400 self-center mr-2">Filter by tag:</span>
                            {allTags.map(tag => (
                                <motion.button
                                    key={tag}
                                    whileHover={{ scale: 1.05, y: -2 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => setSelectedTag(tag === selectedTag ? '' : tag)}
                                    className={`text-xs px-3 py-2 border-2 font-mono font-medium transition-colors duration-200 ${
                                        tag === selectedTag
                                            ? 'bg-accent text-black border-black dark:border-white shadow-brutal-sm'
                                            : 'bg-white dark:bg-zinc-950 text-zinc-700 dark:text-zinc-300 border-zinc-300 dark:border-zinc-600 hover:border-accent hover:text-accent'
                                    }`}
                                >
                                    {tag}
                                </motion.button>
                            ))}
                        </motion.div>
                    )}
                </motion.div>

                {loading && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0.6 }}
                                animate={{ opacity: [0.6, 1, 0.6] }}
                                transition={{ duration: 2, repeat: Infinity }}
                                className="space-y-3"
                            >
                                <div className="aspect-video border-2 border-black dark:border-white bg-zinc-200 dark:bg-zinc-800" />
                                <div className="space-y-2 px-1">
                                    <div className="h-4 bg-zinc-200 dark:bg-zinc-800 w-4/5" />
                                    <div className="h-3 bg-zinc-200 dark:bg-zinc-800 w-full" />
                                    <div className="h-2 bg-zinc-200 dark:bg-zinc-800 w-2/5" />
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
                {!loading && fetchError && (
                    <div className="text-center text-zinc-700 dark:text-zinc-300 mb-8">No videos available right now.</div>
                )}

                <motion.div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" variants={container} initial="hidden" animate={loading ? 'hidden' : 'show'}>
                    {filteredVideos.map((video, i) => (
                        <motion.article key={video._id} variants={item} className={`cursor-pointer pb-1 ${i === 0 ? 'sm:col-span-2' : ''}`} onClick={() => handleClick(video)}>
                            <div className="relative z-0 w-full aspect-[16/9] overflow-hidden group border-2 border-black bg-white shadow-brutal-sm hover:shadow-brutal transition-shadow dark:bg-zinc-950 dark:border-white">
                                {video?.thumbnail?.asset?.url ? (
                                    <>
                                        {/* LQIP background */}
                                        {video.thumbnail.asset.metadata?.lqip && (
                                            <div
                                                className="absolute inset-0 -z-10 bg-center bg-cover blur-sm scale-105 opacity-40"
                                                style={{ backgroundImage: `url(${video.thumbnail.asset.metadata.lqip})` }}
                                            />
                                        )}
                                        <img
                                            src={urlFor(video.thumbnail).width(800).height(450).format('webp').quality(70).url()}
                                            srcSet=""
                                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                                            alt={video.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                                        />
                                    </>
                                ) : (
                                    <div className="text-zinc-700 dark:text-zinc-300 text-sm flex justify-center items-center h-full">No thumbnail</div>
                                )}
                                {/* Provider Badge */}
                                {(() => {
                                    const provider = getProviderInfo(video);
                                    return (
                                        <div className={`absolute top-2 right-2 z-10 ${provider.color} text-white text-xs font-mono font-bold px-2 py-1 shadow-brutal-sm`}>
                                            {provider.name}
                                        </div>
                                    );
                                })()}
                                <div className="absolute inset-0 z-0 pointer-events-none flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <span className="text-white text-5xl drop-shadow">▶</span>
                                </div>
                            </div>
                            <div className="mt-2 px-1 relative z-10">
                                <h2
                                  className={`font-serif font-bold text-black dark:text-white leading-snug ${i === 0 ? 'text-xl' : 'text-sm font-sans font-semibold'}`}
                                  style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                >
                                  {video.title}
                                </h2>
                                {video.description && (
                                    <p
                                      className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5"
                                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                    >
                                      {video.description}
                                    </p>
                                )}
                                <p className="text-xs text-zinc-600 dark:text-zinc-400">
                                    {new Date(video._createdAt).toLocaleDateString(undefined, {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                    })}
                                </p>
                            </div>
                        </motion.article>
                    ))}
                </motion.div>

                <Modal
                    isOpen={!!selectedVideo}
                    onRequestClose={closeModal}
                    contentLabel="Video Modal"
                    className="z-[1001] bg-white dark:bg-zinc-950 dark:text-zinc-100 w-[92vw] md:w-[88vw] max-w-6xl lg:max-w-7xl mx-2 sm:mx-auto mt-10 p-0 shadow-brutal-lg relative border-2 border-black dark:border-white outline-none overflow-hidden"
                    overlayClassName="fixed inset-0 z-[1000] bg-black/80 flex justify-center items-center px-4"
                    onAfterOpen={() => { document.documentElement.classList.add('overflow-hidden'); document.body.classList.add('overflow-hidden'); }}
                    onAfterClose={() => { document.documentElement.classList.remove('overflow-hidden'); document.body.classList.remove('overflow-hidden'); }}
                >
                    <AnimatePresence>
                        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.2 }}>
                            <button
                                ref={closeBtnRef}
                                onClick={closeModal}
                                className="group absolute top-3 right-3 h-10 w-10 md:h-11 md:w-11 bg-white dark:bg-zinc-950 border-2 border-black dark:border-white shadow-brutal-sm flex items-center justify-center hover:bg-accent focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent z-20"
                                aria-label="Close"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" strokeWidth="2" className="h-5 w-5 stroke-black dark:stroke-white">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6l-12 12" />
                                </svg>
                            </button>
                            
                            <div className="w-full h-[60vh] md:h-[70vh] relative bg-black">
                                {filteredVideos.length > 1 && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const idx = filteredVideos.findIndex(v => v._id === selectedVideo?._id);
                                                const prev = filteredVideos[(idx - 1 + filteredVideos.length) % filteredVideos.length];
                                                handleClick(prev);
                                            }}
                                            aria-label="Previous video"
                                            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center shadow transition"
                                        >
                                            <span className="text-2xl">&#8592;</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const idx = filteredVideos.findIndex(v => v._id === selectedVideo?._id);
                                                const next = filteredVideos[(idx + 1) % filteredVideos.length];
                                                handleClick(next);
                                            }}
                                            aria-label="Next video"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center shadow transition"
                                        >
                                            <span className="text-2xl">&#8594;</span>
                                        </button>
                                    </>
                                )}
                                {selectedVideo?.videoType === 'embed' && selectedVideo?.embedUrl ? (
                                    <iframe
                                        src={selectedVideo.embedUrl}
                                        title={selectedVideo.title}
                                        className="absolute inset-0 w-full h-full"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                ) : selectedVideo?.videoType === 'upload' && selectedVideo?.videoFile?.asset?.url ? (
                                    <video
                                        src={selectedVideo.videoFile.asset.url}
                                        className="absolute inset-0 w-full h-full"
                                        controls
                                    />
                                ) : (
                                    <p className="text-zinc-700 text-center">No video available</p>
                                )}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </Modal>
            </div>
        </div>
    );
};

export default Videos;
